/**
 * IterationHistory — the Iterate tab's full build log (owner ask 2026-07-15):
 * every version newest-first, expandable to the FULL prompt, with when the
 * iteration ran (queued → finished), who it is attributed to, the design
 * amendment / settings diff it carried, a failed build's gate notes, and the
 * attached-asset delta vs the previous listed build (diffed from the two
 * versions' assetsSnapshot stamps — versions that predate the stamp show no
 * delta line rather than a guessed one).
 */
import type { Creation, CreationFeedback, CreationVersion, VersionAssetRef } from '@djgg/creations';
import { isMemberOrAdmin, splashReelWarning } from '@djgg/creations';
import { toClaims } from '../auth/model.js';
import { useAuth, useServices } from '../services/context.js';
import { useSubscription } from '../hooks/useSubscription.js';
import { useAttributedAction } from '../hooks/useAttributedAction.js';
import { useSetActiveVersion } from '../hooks/useSetActiveVersion.js';
import { pinnedPlayUrl } from '../services/playUrl.js';
import { pipelineLabel } from './pipelineLabel.js';
import { BuildTraceExpander } from './BuildTracePanel.js';
import { FirstDraftVerdict } from './FirstDraftVerdict.js';

/** Asset-inventory changes between two builds, by attached-asset name. */
export interface AssetDelta {
  added: string[];
  /** Same name, different variantId — the art was replaced in place. */
  updated: string[];
  removed: string[];
}

/**
 * Diff two builds' asset inventories. Null when either side predates the
 * assetsSnapshot stamp (no honest baseline) — the caller renders nothing.
 */
export function diffAssetsSnapshots(
  prev: VersionAssetRef[] | undefined,
  next: VersionAssetRef[] | undefined,
): AssetDelta | null {
  if (!prev || !next) return null;
  const prevByName = new Map(prev.map((a) => [a.name, a]));
  const nextByName = new Map(next.map((a) => [a.name, a]));
  return {
    added: [...nextByName.keys()].filter((name) => !prevByName.has(name)),
    updated: [...nextByName.keys()].filter((name) => {
      const before = prevByName.get(name);
      return before !== undefined && before.variantId !== nextByName.get(name)!.variantId;
    }),
    removed: [...prevByName.keys()].filter((name) => !nextByName.has(name)),
  };
}

/** One human line for a non-empty delta ('' when nothing changed). */
export function formatAssetDelta(delta: AssetDelta): string {
  const part = (verb: string, names: string[]): string =>
    names.length > 0 ? `${names.length} ${verb} (${names.map((n) => `"${n}"`).join(', ')})` : '';
  const parts = [part('added', delta.added), part('replaced', delta.updated), part('removed', delta.removed)]
    .filter(Boolean);
  return parts.join(', ');
}

const fmtWhen = (ms: number | null | undefined): string =>
  ms ? new Date(ms).toLocaleString() : '—';

export function IterationHistory({ creation }: { creation: Creation & { id: string } }) {
  const services = useServices();
  const { user } = useAuth();
  const creationId = creation.id;
  const canManage = isMemberOrAdmin(toClaims(user), creation);
  const { runAttributed, attributionModal } = useAttributedAction();
  const { setActive, activatingVersion, error: activateErr } = useSetActiveVersion(creation, runAttributed);

  // Newest first, straight from the versions subscription (same source as the
  // game page's version-history table).
  const versions = useSubscription<Array<CreationVersion & { id: string }>>(
    (cb) => services.subscribe.versions(creationId, cb),
    [],
    [creationId],
  );

  // Every verdict on the creation, subscribed ONCE (members + superadmins via RLS).
  const feedback = useSubscription<Array<CreationFeedback & { id: string }>>(
    (cb) => services.subscribe.creationFeedback(creationId, cb),
    [],
    [creationId],
  );

  if (versions.length === 0) return null;

  return (
    <div className="panel" style={{ marginTop: 16 }} data-testid="iteration-history">
      <h3 style={{ fontSize: 14, margin: '0 0 4px' }}>Iteration history</h3>
      <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
        Every build of this game, newest first — expand one for the full change request.
        {canManage ? ' A live build that isn’t active yet can be set active here or rolled back to.' : ''}
      </p>
      {activateErr && <div className="error-text" style={{ fontSize: 12 }}>{activateErr}</div>}
      {attributionModal}
      {versions.map((v, i) => {
        // The previous LISTED build (deleted versions leave gaps; the next
        // older row is the honest baseline for the asset delta).
        const prev = versions[i + 1];
        const delta = prev ? diffAssetsSnapshots(prev.assetsSnapshot, v.assetsSnapshot) : null;
        const deltaLine = delta ? formatAssetDelta(delta) : '';
        const failed = v.buildStatus === 'failed';
        // Advisory: a live version without a moving gameplay clip says why,
        // and Explore shows its cover instead (never a blocker).
        const reelWarning = v.buildStatus === 'live' ? splashReelWarning(v.gates?.splashReel) : null;
        return (
          <details key={v.id} data-testid={`iteration-${v.n}`} style={{ padding: '6px 0', borderTop: '1px solid var(--border, #333)' }}>
            <summary style={{ cursor: 'pointer', fontSize: 13 }}>
              <strong>v{v.n}</strong> · {v.buildStatus}
              {' · '}{pipelineLabel(v.pipelineSnapshot)}
              {v.n === creation.activeVersion && ' · active'} · {fmtWhen(v.createdAt)} ·{' '}
              {/* updatedByHandle is '' on legacy rows that predate attribution. */}
              {v.updatedByHandle ? `@${v.updatedByHandle}` : 'unknown'}
              {canManage && v.buildStatus === 'live' && (
                <FirstDraftVerdict creation={creation} version={v.n} rows={feedback} />
              )}
              {v.buildStatus === 'live' && (
                <a
                  className="btn btn--ghost btn--sm"
                  style={{ marginLeft: 8 }}
                  href={pinnedPlayUrl(creationId, v.n)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`iteration-play-${v.n}`}
                >
                  Play v{v.n} ↗
                </a>
              )}
              {canManage && v.buildStatus === 'live' && v.n !== creation.activeVersion && (
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  style={{ marginLeft: 8 }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setActive(v.n);
                  }}
                  disabled={activatingVersion !== null}
                  data-testid={`iteration-set-active-${v.n}`}
                >
                  {activatingVersion === v.n ? 'Setting…' : 'Set active'}
                </button>
              )}
            </summary>
            <div style={{ padding: '6px 0 4px 16px' }}>
              <div className="muted" style={{ fontSize: 12 }}>
                Started {fmtWhen(v.createdAt)} · finished {fmtWhen(v.finishedAt)} · {v.provider}/{v.model}
                {v.actualModel && v.actualModel !== v.model && ` (ran on ${v.actualProvider}/${v.actualModel})`}
              </div>
              {v.prompt ? (
                <pre style={{ fontSize: 12, whiteSpace: 'pre-wrap', margin: '6px 0 0' }} data-testid={`iteration-prompt-${v.n}`}>
                  {v.prompt}
                </pre>
              ) : (
                <p className="muted" style={{ fontSize: 12, margin: '6px 0 0' }}>
                  No change request — {v.diffSummary || 'a platform-initiated build'}.
                </p>
              )}
              {v.designAmendment && (
                <p className="muted" style={{ fontSize: 12, margin: '6px 0 0' }}>
                  Design amended: {v.designAmendment}
                </p>
              )}
              {v.settingsDiff && (
                <pre className="muted" style={{ fontSize: 12, whiteSpace: 'pre-wrap', margin: '6px 0 0' }}>
                  Settings changed: {v.settingsDiff}
                </pre>
              )}
              {deltaLine && (
                <p className="muted" style={{ fontSize: 12, margin: '6px 0 0' }} data-testid={`iteration-assets-${v.n}`}>
                  Assets since v{prev!.n}: {deltaLine}
                </p>
              )}
              {reelWarning && (
                <p className="muted" style={{ fontSize: 12, margin: '6px 0 0' }} data-testid={`iteration-reel-warning-${v.n}`}>
                  {reelWarning}
                </p>
              )}
              {failed && v.gates?.notes && (
                <pre className="error-text" style={{ fontSize: 12, whiteSpace: 'pre-wrap', margin: '6px 0 0' }}>
                  {v.gates.notes}
                </pre>
              )}
              <BuildTraceExpander
                creationId={creationId}
                version={v.n}
                available={!!v.traceGcsUri || v.pipelineSnapshot?.config?.construction === 'measured-first-playable'}
              />
            </div>
          </details>
        );
      })}
    </div>
  );
}
